import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Lock, Mail } from "lucide-react";
import LanguageSelector from "../components/LanguageSelector";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success(t('common.success'));
    } catch (error) {
      const message = error.response?.data?.detail || t('auth.invalidCredentials');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 dark:bg-background flex">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector className="bg-white/90 dark:bg-card backdrop-blur-sm" />
      </div>

      {/* Left Side - Branding with technical image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=1920')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-800/85" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 inline-block">
            <img 
              src={LOGO_URL} 
              alt="plutec" 
              className="w-64"
            />
          </div>
          <h1 className="font-manrope font-bold text-4xl leading-tight mb-4">
            {t('branding.title', 'Alarmübertragungssystem')}<br />
            <span className="text-blue-400">comXline</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-md">
            {t('branding.subtitle', 'Professionelle Konfiguration von Anwahlfolgen für Telenot-Übertragungseinrichtungen.')}
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="w-12 h-1 bg-blue-500 rounded"></div>
            <span className="text-sm text-slate-400">{t('branding.tagline', 'Sichere Übertragung. Zuverlässige Alarmierung.')}</span>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 dark:bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div className="bg-slate-900 dark:bg-card rounded-xl p-4 mb-4">
              <img 
                src={LOGO_URL} 
                alt="plutec" 
                className="w-40"
              />
            </div>
            <p className="text-sm text-slate-500 dark:text-muted-foreground">{t('branding.title', 'Alarmübertragungssystem')} comXline</p>
          </div>

          <Card className="card-base shadow-xl" data-testid="login-card">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="font-manrope text-2xl font-bold text-slate-900 dark:text-foreground">
                {t('auth.loginTitle')}
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-muted-foreground">
                {t('auth.loginSubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="label-style">
                    {t('common.email')}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="label-style">
                    {t('common.password')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1e3a5f] hover:bg-[#0d2137] dark:bg-blue-600 dark:hover:bg-blue-700 text-white h-11 mt-2"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? t('common.loading') : t('common.login')}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/reset-password"
                  className="text-sm text-[#1e3a5f] dark:text-blue-400 hover:underline"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <div className="mt-6 text-center text-sm text-slate-500 dark:text-muted-foreground">
                {t('auth.noAccount')}{" "}
                <Link
                  to="/register"
                  className="text-[#1e3a5f] dark:text-blue-400 hover:underline font-medium"
                >
                  {t('auth.registerLink')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
